import { useAuth } from '@agentnet/auth';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { IdentityVerificationResult } from 'api/file/interfaces/get-file-order-insights';
import { generateIntelliCheckReport } from 'api/pdf/pdf-api';
import { identityReportAvailableText, oldVerificationRequestText } from 'features/help/constants';
import useAsync from 'hooks/useAsync';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import PDFIcon from 'ui-kit/icons/PDF';
import { formatDate, getVerificationExpiryDate, isPastExpiry } from 'utilities/dateFormatter';
import OrderInsightRisks from './OrderInsightRisks';
import StatusRow from './StatusRow';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  riskTypography: {
    '& a': {
      color: '#5F2120',
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  buttonSpacing: {
    marginBottom: theme.spacing(2),
  },
}));

const VerificationResults = ({
  transactionStatus,
  transactionId = '',
  summary,
  submitted,
  attempt,
  isPdf = false,
  enhancedIdentityVerification,
  fileId,
  enhancedIdentityVerificationStatus,
  index = 0,
}: IdentityVerificationResult & {
  fileId?: string;
  enhancedIdentityVerificationStatus?: 'idle' | 'pending' | 'success' | 'error';
  index?: number;
}) => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const {
    customerName,
    transactionDate = '',
    barcodeRecognized,
    expiredId,
    ocrBarcodeMatches,
    documentLivenessFront,
    selfie,
    deviceAddress,
    phoneNumber,
    risks = [],
  } = summary ?? {};

  const statuses = [
    { label: 'Name on ID:', value: customerName, noStatus: true },
    { label: 'Transaction Date:', value: formatDate(transactionDate, 'full'), noStatus: true },
    { label: 'Back of ID Barcode Recognized:', value: barcodeRecognized },
    { label: 'Expired ID:', value: expiredId },
    { label: 'Printed Data on ID Matches Barcode Data:', value: ocrBarcodeMatches },
    { label: 'Live ID Document:', value: documentLivenessFront },
    { label: 'Selfie:', value: selfie },
    { label: 'Device Address:', value: deviceAddress },
    { label: 'Phone Number:', value: phoneNumber },
  ];

  const expiryDateFormatted = getVerificationExpiryDate(transactionDate);
  const isOlderThan90Days = isPastExpiry(transactionDate);
  const identityReportMessage = !isOlderThan90Days
    ? identityReportAvailableText.replace('expiryDate', expiryDateFormatted)
    : oldVerificationRequestText;

  const handleOpenVerificationReport = async () => {
    const token = await getAccessToken();
    return generateIntelliCheckReport(fileId ?? '', transactionId, token, addGlobalMsg);
  };

  const { execute: executeIntelliCheckReport, status: intelliCheckReportStatus } = useAsync(
    handleOpenVerificationReport,
    false,
  );

  const handleButtonClick = () => {
    if (!isOlderThan90Days) {
      executeIntelliCheckReport().then();
    } else {
      enhancedIdentityVerification?.();
    }
  };

  return (
    <Box>
      <Box className={classes.container}>
        {statuses.map((item, idx) => (
          <StatusRow
            key={idx}
            index={idx}
            label={item.label}
            value={item.value}
            isPlainText={item.noStatus}
            isPdf={isPdf}
          />
        ))}
      </Box>
      <OrderInsightRisks stringAlerts={risks} isPdf={isPdf} hasStringHtml />

      {!isPdf && (
        <Box className={classes.container}>
          <Typography
            className={classes.buttonSpacing}
            variant="body1"
            dangerouslySetInnerHTML={{ __html: identityReportMessage }}
          />
          <AgentNetButton
            variant="outlined"
            color="primary"
            data-testid={
              !isOlderThan90Days
                ? `identity-verification-button-identity-verification-report-${index}`
                : `identity-verification-button-resend-verification-request-${index}`
            }
            onClick={handleButtonClick}
            startIcon={!isOlderThan90Days ? <PDFIcon /> : undefined}
            loading={enhancedIdentityVerificationStatus === 'pending' || intelliCheckReportStatus === 'pending'}
            disabled={
              enhancedIdentityVerificationStatus === 'pending' ||
              intelliCheckReportStatus === 'pending' ||
              !transactionId
            }
          >
            {!isOlderThan90Days ? 'Identity Verification Report' : 'Resend Verification Request'}
          </AgentNetButton>
        </Box>
      )}
    </Box>
  );
};

export default VerificationResults;
